import { Link } from 'react-router-dom';
import { themeClasses } from '@member-portal/ui';
import { useAuth } from '@/contexts/AuthContext';

const teamMembers = [
  {
    name: 'Tony Shepherd',
    title: 'CEO and Partner',
    bio: 'Tony oversees the operations of both Four Hundred and Four Hundred by Design as well as growing the travel side of the business.',
    image: '/images/about-us/Tony_Shepherd_Headshot.jpeg',
    fullBio: `Tony, CEO of Four Hundred, is a luxury travel leader with a career spanning high-profile roles in hospitality and travel. He scaled Protravel International to $1 billion and grew Virtuoso’s Europe and Middle East network to €2 billion. He now drives growth at Four Hundred, elevating luxury across its brands.`,
  },
  {
    name: 'Tony Abrams',
    title: 'Founder and Partner',
    bio: "Tony is the founder of Four Hundred and the creative force behind the company's business, brand and culture.",
    image: '/images/about-us/Tony_Abrams_Headshot.jpeg',
    fullBio: `Originally from South Florida and based in New York for 20+ years, Tony is the founder of Four Hundred, an exclusive lifestyle management firm. A Fordham graduate, he began his career in entertainment, working on The West Wing and Sex and the City. In 2007, he founded Four Hundred to deliver unparalleled luxury and service. In 2024, Abrams founded STELLAR, a platform designed to reduce economic waste by enabling users to buy and sell non-refundable event tickets, travel accommodations, and dining reservations.`,
  },
  {
    name: 'Karen Goldberg',
    title: 'SVP, Brand Strategy & Business Development',
    bio: 'Karen leads the development of the Four Hundred by Design division.',
    image: '/images/about-us/Karen_Goldberg_Headshot.jpeg',
    fullBio: `Karen Goldberg's expertise in brand strategy has been instrumental in positioning Four Hundred by Design as the premier platform for luxury travel advisors. Her strategic initiatives have driven significant growth in partnerships with elite travel partners and agencies worldwide.`,
  },
  {
    name: 'Skie Ocasio',
    title: 'Chief Operating Officer',
    bio: 'Skie oversees all operations, logistics, brand and marketing efforts for FHxD.',
    image: '/images/about-us/Skie_Ocasio_Headshot.jpeg',
    fullBio: `Skie is a distinguished luxury lifestyle expert with over 15 years of experience in concierge and travel services. Having worked with globally renowned brands, he has consistently delivered exceptional, bespoke experiences to the world’s most discerning clients. With a profound understanding of the needs of ultra-high-net-worth individuals, Skie leverages the power of CRM and AI technology to create personalized services that are both innovative and efficient.`,
  },
];

function AboutPage() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="fhxd-chrome-top">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/images/agnieszka-stankiewicz-h7Vf6TriiWw-unsplash.jpg)' }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-title font-normal text-3xl md:text-5xl lg:text-7xl tracking-wider uppercase text-white mb-8">
            About Us
          </h1>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-theme-bg-secondary">
        <div className={themeClasses.container}>
          <h2 className={`${themeClasses.title.h2} text-center mb-16`}>
            Our Team
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 lg:gap-20">
            {teamMembers.map((member, index) => (
              <TeamMemberCard key={index} member={member} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Only show when not logged in */}
      {!isAuthenticated && (
        <section className="py-24 bg-theme-bg border-t border-theme-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className={`${themeClasses.title.h3} mb-6`}>
              READY TO GET STARTED?
            </h2>
            <p className={`${themeClasses.text.body} text-lg mb-8`}>
              Discover the FHxD world-class travel partner collection and earn more, faster
            </p>
            <Link
              to="/signup"
              className={themeClasses.button.primary}
            >
              Advisor Sign Up
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}

interface TeamMember {
  name: string;
  title: string;
  image: string;
  fullBio: string;
}

function TeamMemberCard({ member }: { member: TeamMember }) {
  return (
    <div className="group">
      <div className="flex flex-col sm:flex-row gap-8">
        {/* Image */}
        <div className="flex-shrink-0">
          <div className="w-40 h-40 bg-theme-bg overflow-hidden">
            <img
              src={member.image}
              alt={member.name}
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <h3 className={`${themeClasses.title.h5} mb-1`}>
            {member.name}
          </h3>
          <p className={`${themeClasses.text.caption} mb-4`}>
            {member.title}
          </p>
          <p className={`${themeClasses.text.muted} leading-relaxed`}>
            {member.fullBio}
          </p>
        </div>
      </div>
    </div>
  );
}

export default AboutPage;
